import React, { useState, useEffect } from "react";
import { User, Calendar, Clock, MapPin, Plus, Trash2, X, BookmarkCheck, History } from "lucide-react";

interface LifeEvent {
  id: string;
  date: string;
  title: string;
  category: "CAREER" | "RELATIONSHIP" | "FINANCIAL" | "HEALTH" | "CRISIS";
}

interface NatalIntakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveProfile: (profile: { name: string; birthDate: string; birthTime: string; city: string }) => void;
  onSelectEventDate: (dateString: string) => void;
}

export const NatalIntakeModal: React.FC<NatalIntakeModalProps> = ({
  isOpen,
  onClose,
  onSaveProfile,
  onSelectEventDate,
}) => {
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("1995-10-24");
  const [birthTime, setBirthTime] = useState("14:35");
  const [city, setCity] = useState("San Francisco, CA");

  const [events, setEvents] = useState<LifeEvent[]>([]);
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventDate, setNewEventDate] = useState("");
  const [newEventCategory, setNewEventCategory] = useState<LifeEvent["category"]>("CAREER");

  // Load from localStorage on mount
  useEffect(() => {
    const savedProfile = localStorage.getItem("nex_natal_profile");
    if (savedProfile) {
      try {
        const p = JSON.parse(savedProfile);
        setName(p.name || "");
        setBirthDate(p.birthDate || "1995-10-24");
        setBirthTime(p.birthTime || "14:35");
        setCity(p.city || "San Francisco, CA");
      } catch (e) {}
    }

    const savedEvents = localStorage.getItem("nex_life_events");
    if (savedEvents) {
      try {
        setEvents(JSON.parse(savedEvents));
      } catch (e) {}
    } else {
      // Default Seed Milestone Events
      setEvents([
        { id: "1", date: "2018-06-15", title: "Promoted to Executive Director", category: "CAREER" },
        { id: "2", date: "2020-03-20", title: "Met Life Partner", category: "RELATIONSHIP" },
        { id: "3", date: "2022-11-10", title: "Severe Burnout & Liquidation", category: "CRISIS" },
      ]);
    }
  }, []);

  if (!isOpen) return null;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const profile = { name, birthDate, birthTime, city };
    localStorage.setItem("nex_natal_profile", JSON.stringify(profile));
    onSaveProfile(profile);
    onClose();
  };

  const handleAddEvent = () => {
    if (!newEventTitle || !newEventDate) return;
    const updated = [
      ...events,
      {
        id: Date.now().toString(),
        title: newEventTitle,
        date: newEventDate,
        category: newEventCategory,
      },
    ];
    setEvents(updated);
    localStorage.setItem("nex_life_events", JSON.stringify(updated));
    setNewEventTitle("");
    setNewEventDate("");
  };

  const handleDeleteEvent = (id: string) => {
    const updated = events.filter((e) => e.id !== id);
    setEvents(updated);
    localStorage.setItem("nex_life_events", JSON.stringify(updated));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div className="glass-panel w-full max-w-2xl rounded-2xl border border-cyan-500/30 overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
        
        {/* HEADER */}
        <div className="p-5 border-b border-gray-800 flex items-center justify-between bg-gray-900/60">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-wider text-white uppercase flex items-center gap-2 font-mono">
                NATAL INTAKE & <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300">CHRONOMETER</span>
              </h2>
              <p className="text-xs text-gray-400 font-mono">Precision Ephemeris Initialization • Life Event Tracker</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 overflow-y-auto flex flex-col gap-6">
          
          {/* 1. NATAL BIRTH DATA FORM */}
          <form onSubmit={handleSave} className="flex flex-col gap-4">
            <h3 className="text-xs font-mono font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
              <BookmarkCheck className="w-4 h-4" /> Cosmic Natal Parameters
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-mono text-gray-400 block mb-1">Full Name / Subject ID</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Alexander Vance"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-cyan-400"
                />
              </div>

              <div>
                <label className="text-[11px] font-mono text-gray-400 block mb-1">Birth City / Coordinates</label>
                <div className="relative">
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="San Francisco, CA"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2.5 pl-8 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                  <MapPin className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-3" />
                </div>
              </div>

              <div>
                <label className="text-[11px] font-mono text-gray-400 block mb-1">Exact Birth Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2.5 pl-8 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                  <Calendar className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-3" />
                </div>
              </div>

              <div>
                <label className="text-[11px] font-mono text-gray-400 block mb-1">Exact Birth Time (Local)</label>
                <div className="relative">
                  <input
                    type="time"
                    value={birthTime}
                    onChange={(e) => setBirthTime(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2.5 pl-8 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                  <Clock className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-3" />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold text-xs font-mono transition"
            >
              LOCK NATAL COORDINATES & RECALCULATE SPHERE
            </button>
          </form>

          <hr className="border-gray-800" />

          {/* 2. LIFE EVENT CHRONOMETER (RETENTION ENGINE) */}
          <div className="flex flex-col gap-3">
            <h3 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
              <History className="w-4 h-4" /> Life Event Chronometer (Historical Correlator)
            </h3>
            <p className="text-[11px] text-gray-400 font-sans">
              Log major life milestones. Click any event to scrub the 3D celestial sphere to that exact date and reveal what planetary transits caused it.
            </p>

            {/* Add Event Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 bg-gray-900/60 p-3 rounded-xl border border-gray-800">
              <input
                type="text"
                value={newEventTitle}
                onChange={(e) => setNewEventTitle(e.target.value)}
                placeholder="Milestone (e.g. Signed Big Contract)"
                className="bg-gray-950 border border-gray-700 rounded p-2 text-xs text-white col-span-2"
              />
              <input
                type="date"
                value={newEventDate}
                onChange={(e) => setNewEventDate(e.target.value)}
                className="bg-gray-950 border border-gray-700 rounded p-2 text-xs text-white"
              />
              <button
                type="button"
                onClick={handleAddEvent}
                className="rounded bg-amber-500 hover:bg-amber-400 text-gray-950 font-bold text-xs font-mono flex items-center justify-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> PIN EVENT
              </button>
            </div>

            {/* Logged Events List */}
            <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto pr-1">
              {events.map((ev) => (
                <div
                  key={ev.id}
                  className="flex items-center justify-between p-2.5 rounded-lg bg-gray-900 border border-gray-800 hover:border-cyan-500/50 transition group cursor-pointer"
                  onClick={() => {
                    onSelectEventDate(ev.date);
                    onClose();
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded bg-gray-800 text-gray-300 font-mono">{ev.date}</span>
                    <span className="text-xs text-white font-medium group-hover:text-cyan-300 transition">{ev.title}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteEvent(ev.id);
                    }}
                    className="p-1 text-gray-500 hover:text-rose-400 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};
